import { CostLayerKind } from "@redotech/crdb-enums/inventory/cost-layer-kind";
import { ImsCostingMethod } from "@redotech/crdb-enums/inventory/ims-costing-method";
import { costLayerIdFormat } from "@redotech/redo-inventory-id";
import {
  productFamilyIdFormat,
  productIdFormat,
} from "@redotech/redo-product-id";
import { AdvancedFilterDataSchema } from "@redotech/redo-views-common/advanced-filters/advanced-filter-data";
import { SortDirectionSchema } from "@redotech/redo-views-common/table";
import * as z from "zod";
import { FractionalAmountSchema } from "./cost-report-amount";

export const InventoryValuationSortKeySchema = z.enum([
  "value",
  "onHand",
  "sku",
]);
export type InventoryValuationSortKey = z.infer<
  typeof InventoryValuationSortKeySchema
>;

const ValuationLayerRowSchema_ = z.object({
  id: costLayerIdFormat.schema,
  kind: z.enum(CostLayerKind),
  receivedAt: z.date(),
  source: z.string(),
  locationName: z.string().nullable(),
  remaining: z.number().int(),
  unitCostFractional: FractionalAmountSchema.nullable(),
  valueFractional: FractionalAmountSchema.nullable(),
  currency: z.string(),
});
export const ValuationLayerRowSchema: z.ZodType<ValuationLayerRow> =
  ValuationLayerRowSchema_;
export interface ValuationLayerRow extends z.infer<
  typeof ValuationLayerRowSchema_
> {}

const ValuationProductRowSchema_ = z.object({
  productId: productIdFormat.schema,
  productFamilyId: productFamilyIdFormat.schema.nullable(),
  sku: z.string().nullable(),
  title: z.string(),
  onHand: z.number().int(),
  averageUnitCostFractional: FractionalAmountSchema.nullable(),
  valueFractional: FractionalAmountSchema,
  currency: z.string(),
  costingMethod: z.enum(ImsCostingMethod),
  layers: z.array(ValuationLayerRowSchema),
});
export const ValuationProductRowSchema: z.ZodType<ValuationProductRow> =
  ValuationProductRowSchema_;
export interface ValuationProductRow extends z.infer<
  typeof ValuationProductRowSchema_
> {}

/** Products with stock on hand now. Filters: "sku" only. */
export const getInventoryValuationRows = {
  name: "getInventoryValuationRows" as const,
  input: z.object({
    page: z.number().int().min(0).default(0),
    pageSize: z.number().int().min(1).max(1000),
    sortBy: InventoryValuationSortKeySchema.optional(),
    sortDirection: SortDirectionSchema.optional(),
    advancedFiltersData: z.array(AdvancedFilterDataSchema).optional(),
  }),
  output: z.object({ rows: z.array(ValuationProductRowSchema) }),
};

/** total counts the filtered products. */
export const getInventoryValuationSummary = {
  name: "getInventoryValuationSummary" as const,
  input: z.object({
    advancedFiltersData: z.array(AdvancedFilterDataSchema).optional(),
  }),
  output: z.object({
    total: z.number().int(),
    valueFractional: FractionalAmountSchema,
    units: z.number().int(),
    currency: z.string(),
  }),
};
